import { type FormEvent, useEffect, useState } from "react";
import { formatDate } from "@/lib/date";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { type Member } from "@/hooks/useMembers";
import { useMembersPaginated } from "@/hooks/useMembersPaginated";
import { usePageState } from "@/hooks/usePageState";
import { useResetPageOnChange } from "@/hooks/useResetPageOnChange";
import { useSearchQueryState } from "@/hooks/useSearchQueryState";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { mapApiError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarDays, KeyRound, Loader2, Mail, Package, Plus, Search, Send, Shield, ShieldCheck, Trash2, UserCheck, UserX, Users } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MemberAvatar } from "@/components/ui/avatar-initials";
import { TablePagination } from "@/components/ui/table-pagination";

const ROLE_STYLES: Record<string, { classes: string; icon?: React.ReactNode }> = {
  admin:       { classes: "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700", icon: <Shield className="h-3 w-3" /> },
  lead:        { classes: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700" },
  contributor: { classes: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700" },
  member:      { classes: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700" },
  viewer:      { classes: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700" },
};

function roleBadgeClasses(role: string): string {
  return ROLE_STYLES[role]?.classes ?? "bg-muted text-muted-foreground border-border";
}

function RoleBadge({ role }: { role: string }) {
  const style = ROLE_STYLES[role];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${roleBadgeClasses(role)}`}>
      {style?.icon}
      {role}
    </span>
  );
}

const STATUS_CONFIG: Record<Member["status"], { labelKey: string; dot: string; text: string }> = {
  active:  { labelKey: "members.status.active",  dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400" },
  pending: { labelKey: "members.status.pending", dot: "bg-amber-400",   text: "text-amber-700 dark:text-amber-400" },
  revoked: { labelKey: "members.status.revoked", dot: "bg-red-400",     text: "text-red-600 dark:text-red-400" },
};

function StatusBadge({ status }: { status: Member["status"] }) {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
      {t(cfg.labelKey)}
    </span>
  );
}

function MemberProfileCard() {
  const { user } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">{t("members.myProfile.title")}</h2>
        <p className="text-muted-foreground">{t("members.myProfile.subtitle")}</p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm text-muted-foreground">{t("members.myProfile.name")}</dt>
              <dd className="text-sm font-medium">{user?.name}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">{t("members.myProfile.email")}</dt>
              <dd className="text-sm font-medium">{user?.email}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">{t("members.myProfile.role")}</dt>
              <dd>
                <Badge variant="secondary">{user?.role}</Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">{t("members.myProfile.organization")}</dt>
              <dd className="text-sm font-medium">{user?.org_name}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

export function MembersPage() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return <MemberProfileCard />;
  return <AdminMembersView />;
}

function AdminMembersView() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { page, pageSize, totalPages, setPage, handlePageSizeChange } = usePageState(10);
  const [search, setSearch] = useSearchQueryState();
  const debounced = useDebouncedValue(search, 300);
  useResetPageOnChange(debounced, setPage);
  const { members: paginatedMembers, total, activeCount, pendingCount, loading, error, refetch } = useMembersPaginated(page, pageSize, debounced);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<Member | null>(null);
  const [roleChangePending, setRoleChangePending] = useState<{
    member: Member;
    newRole: string;
  } | null>(null);
  const [resending, setResending] = useState<Set<string>>(new Set());
  const [reactivating, setReactivating] = useState<Set<string>>(new Set());
  const [deletePending, setDeletePending] = useState<Member | null>(null);
  const [openRoleSelectId, setOpenRoleSelectId] = useState<string | null>(null);
  const [entitlementTarget, setEntitlementTarget] = useState<Member | null>(null);

  async function handleResendInvite(memberId: string) {
    setResending((prev) => new Set(prev).add(memberId));
    try {
      await api(`/api/v2/members/${memberId}/resend-invite`, { method: "POST" });
      toast.success(t("members.toast.resendSuccess"));
    } catch (err) {
      toast.error(
        mapApiError(err, { 429: t("members.toast.resendRateLimited") }, t("members.toast.resendError")),
      );
    } finally {
      setResending((prev) => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });
    }
  }

  async function handleReactivate(memberId: string) {
    setReactivating((prev) => new Set(prev).add(memberId));
    try {
      await api(`/api/v2/members/${memberId}/reactivate`, { method: "POST" });
      toast.success(t("members.toast.reactivateSuccess"));
      refetch();
    } catch (err) {
      toast.error(
        mapApiError(
          err,
          {
            404: t("members.toast.reactivateNotFound"),
            403: t("members.toast.reactivateForbidden"),
          },
          t("members.toast.reactivateError"),
        ),
      );
    } finally {
      setReactivating((prev) => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });
    }
  }

  async function handleDeleteConfirm() {
    if (!deletePending) return;
    const member = deletePending;
    setDeletePending(null);
    try {
      await api(`/api/v2/members/${member.id}/permanent`, { method: "DELETE" });
      toast.success(t("members.toast.deleteSuccess"));
      refetch();
    } catch (err) {
      toast.error(
        mapApiError(
          err,
          { 403: t("members.toast.deleteForbidden"), 404: t("members.toast.deleteNotFound") },
          t("members.toast.deleteError"),
        ),
      );
    }
  }

  async function handleRoleChangeConfirm() {
    if (!roleChangePending) return;
    const { member, newRole } = roleChangePending;
    setRoleChangePending(null);
    try {
      await api(`/api/v2/members/${member.id}`, {
        method: "PATCH",
        body: JSON.stringify({ role: newRole }),
      });
      toast.success(t("members.toast.roleUpdateSuccess"));
      refetch();
    } catch (err) {
      toast.error(
        mapApiError(
          err,
          { 403: t("members.toast.roleUpdateForbidden") },
          t("members.toast.roleUpdateError"),
        ),
      );
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Users}
        title={t("members.header.title")}
        subtitle={t("members.header.subtitle")}
        stats={
          !loading && total > 0
            ? [
                { value: total, label: t("members.stats.total") },
                ...(activeCount > 0
                  ? [{ value: activeCount, label: t("members.stats.active"), tone: "success" as const }]
                  : []),
                ...(pendingCount > 0
                  ? [{ value: pendingCount, label: t("members.stats.pending"), tone: "warning" as const }]
                  : []),
              ]
            : undefined
        }
        action={
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5 shadow-sm">
                <Plus className="h-4 w-4" />
                {t("members.inviteButton")}
              </Button>
            </DialogTrigger>
            {inviteOpen && (
              <InviteDialog
                onSuccess={() => { setInviteOpen(false); refetch(); }}
              />
            )}
          </Dialog>
        }
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder={t("members.search.placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {!loading && total === 0 ? (
        <EmptyState
          icon={Users}
          title={t("members.empty.title")}
          description={t("members.empty.description")}
          action={{ label: t("members.inviteButton"), onClick: () => setInviteOpen(true) }}
        />
      ) : (
        <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[220px]">
                <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{t("members.table.member")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{t("members.table.email")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />{t("members.table.role")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-muted-foreground/50" />{t("members.table.status")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />{t("members.table.entitlements")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" />{t("members.table.apiKey")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{t("members.table.joined")}</span>
              </TableHead>
              <TableHead className="w-[200px] text-right" />
            </TableRow>
          </TableHeader>
          {loading ? (
            <TableSkeleton columns={8} />
          ) : (
          <TableBody>
            {paginatedMembers.map((m) => (
              <TableRow key={m.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <MemberAvatar name={m.name} />
                    <span className="font-medium">{m.name}</span>
                  </div>
                </TableCell>
                <TableCell>{m.email}</TableCell>
                <TableCell>
                  {m.email === user?.email || m.status === "revoked" ? (
                    <RoleBadge role={m.role} />
                  ) : (
                    <Select
                      value={m.role}
                      onValueChange={(newRole) => {
                        setOpenRoleSelectId(null);
                        setRoleChangePending({ member: m, newRole });
                      }}
                      open={openRoleSelectId === m.id}
                      onOpenChange={(o) => setOpenRoleSelectId(o ? m.id : null)}
                    >
                      <SelectTrigger className={`h-7 w-auto gap-1 rounded-full border px-2 text-xs font-semibold ${roleBadgeClasses(m.role)}`}>
                        {ROLE_STYLES[m.role]?.icon}
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge status={m.status} />
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    className="inline-flex flex-wrap gap-1 rounded-md px-1 py-0.5 hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => setEntitlementTarget(m)}
                  >
                    {m.entitlements.length > 0 ? (
                      m.entitlements.map((ent) => (
                        <Badge key={ent} variant="secondary" className="text-xs">
                          {ent}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">&mdash;</span>
                    )}
                  </button>
                </TableCell>
                <TableCell>
                  {m.has_key ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      <KeyRound className="h-3.5 w-3.5" />
                      {t("members.apiKey.active")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <KeyRound className="h-3.5 w-3.5 opacity-30" />
                      {t("members.apiKey.none")}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(m.created_at)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1 flex-nowrap">
                    {m.status === "pending" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t("members.actions.resendAria")}
                        disabled={resending.has(m.id)}
                        onClick={() => handleResendInvite(m.id)}
                      >
                        {resending.has(m.id) ? (
                          <Loader2 className="animate-spin h-4 w-4" />
                        ) : (
                          <><Send className="h-4 w-4" />{t("members.actions.resend")}</>
                        )}
                      </Button>
                    )}
                    {m.status === "revoked" && m.email !== user?.email && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t("members.actions.reactivateAria")}
                        disabled={reactivating.has(m.id)}
                        onClick={() => handleReactivate(m.id)}
                      >
                        {reactivating.has(m.id) ? (
                          <Loader2 className="animate-spin h-4 w-4" />
                        ) : (
                          <><UserCheck className="h-4 w-4" />{t("members.actions.reactivate")}</>
                        )}
                      </Button>
                    )}
                    {m.status === "active" && m.email !== user?.email && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setConfirmTarget(m)}
                      >
                        <UserX className="h-4 w-4" />
                        {t("members.actions.deactivate")}
                      </Button>
                    )}
                    {m.email !== user?.email && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeletePending(m)}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t("members.actions.remove")}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          )}
        </Table>
        <TablePagination
          currentPage={page}
          totalPages={totalPages(total)}
          onPrev={() => setPage((p) => p - 1)}
          onNext={() => setPage((p) => p + 1)}
          hasNext={page < totalPages(total)}
          hasPrev={page > 1}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
        </div>
      )}

      <DeactivateDialog
        member={confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirm={async () => {
          if (!confirmTarget) return;
          try {
            await api(`/api/v2/members/${confirmTarget.id}`, { method: "DELETE" });
            toast.success(t("members.toast.deactivateSuccess"));
            setConfirmTarget(null);
            refetch();
          } catch {
            toast.error(t("members.toast.deactivateError"));
            setConfirmTarget(null);
          }
        }}
      />
      <RoleChangeDialog
        pending={roleChangePending}
        onClose={() => setRoleChangePending(null)}
        onConfirm={handleRoleChangeConfirm}
      />
      <DeleteMemberDialog
        member={deletePending}
        onClose={() => setDeletePending(null)}
        onConfirm={handleDeleteConfirm}
      />
      <EditEntitlementsDialog
        member={entitlementTarget}
        onClose={() => setEntitlementTarget(null)}
        onSave={async (member, entitlements) => {
          try {
            await api(`/api/v2/members/${member.id}`, {
              method: "PATCH",
              body: JSON.stringify({ entitlements }),
            });
            toast.success(t("members.toast.entitlementsUpdateSuccess"));
            setEntitlementTarget(null);
            refetch();
          } catch {
            toast.error(t("members.toast.entitlementsUpdateError"));
          }
        }}
      />
    </div>
  );
}

const ROLES = ["admin", "lead", "contributor", "member", "viewer"] as const;
const KNOWN_ENTITLEMENTS = ["raise-pmo"] as const;

function InviteDialog({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [accountId, setAccountId] = useState("");
  const [selectedEntitlements, setSelectedEntitlements] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  function toggleEntitlement(ent: string) {
    setSelectedEntitlements((prev) => {
      const next = new Set(prev);
      if (next.has(ent)) {
        next.delete(ent);
      } else {
        next.add(ent);
      }
      return next;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api("/api/v2/members", {
        method: "POST",
        body: JSON.stringify({ email, name, role, account_id: accountId, entitlements: Array.from(selectedEntitlements) }),
      });
      setEmail("");
      setName("");
      setRole("member");
      setAccountId("");
      setSelectedEntitlements(new Set());
      toast.success(t("members.toast.inviteSuccess"));
      onSuccess();
    } catch (err) {
      toast.error(
        mapApiError(err, { 409: t("members.toast.inviteConflict") }, t("members.toast.inviteError")),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t("members.dialogs.invite.title")}</DialogTitle>
        <DialogDescription>{t("members.dialogs.invite.description")}</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="invite-email">{t("members.dialogs.invite.emailLabel")}</Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-name">{t("members.dialogs.invite.nameLabel")}</Label>
          <Input
            id="invite-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-role">{t("members.dialogs.invite.roleLabel")}</Label>
          <Select value={role} onValueChange={setRole} disabled={submitting}>
            <SelectTrigger id="invite-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-account-id">{t("members.dialogs.invite.accountIdLabel")} <span className="text-muted-foreground text-xs">{t("members.dialogs.invite.accountIdOptional")}</span></Label>
          <Input
            id="invite-account-id"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            placeholder={t("members.dialogs.invite.accountIdPlaceholder")}
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label>{t("members.dialogs.invite.entitlementsLabel")}</Label>
          <div className="space-y-2">
            {KNOWN_ENTITLEMENTS.map((ent) => (
              <label key={ent} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedEntitlements.has(ent)}
                  onChange={() => toggleEntitlement(ent)}
                  disabled={submitting}
                  className="h-4 w-4 rounded border-input"
                  aria-label={ent}
                />
                <span className="text-sm">{ent}</span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="animate-spin" />
                {t("members.dialogs.invite.submitting")}
              </>
            ) : (
              t("members.dialogs.invite.submit")
            )}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function DeactivateDialog({
  member,
  onClose,
  onConfirm,
}: {
  member: Member | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("members.dialogs.deactivate.title")}</DialogTitle>
          <DialogDescription>
            <Trans i18nKey="members.dialogs.deactivate.description" values={{ name: member?.name }}>
              This will deactivate <strong>{member?.name}</strong> and revoke all their API keys. This action cannot be undone.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("members.dialogs.deactivate.cancel")}</Button>
          <Button variant="destructive" onClick={onConfirm}>{t("members.dialogs.deactivate.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleChangeDialog({
  pending,
  onClose,
  onConfirm,
}: {
  pending: { member: Member; newRole: string } | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={!!pending} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("members.dialogs.roleChange.title")}</DialogTitle>
          <DialogDescription>
            {t("members.dialogs.roleChange.descriptionPrefix")}
            <strong>{pending?.member.name}</strong>
            {t("members.dialogs.roleChange.descriptionMiddle")}
            <strong>{pending?.newRole}</strong>
            {t("members.dialogs.roleChange.descriptionSuffix")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("members.dialogs.roleChange.cancel")}</Button>
          <Button onClick={onConfirm}>{t("members.dialogs.roleChange.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteMemberDialog({
  member,
  onClose,
  onConfirm,
}: {
  member: Member | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <AlertDialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("members.dialogs.remove.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("members.dialogs.remove.descriptionPrefix")}
            <strong>{t("members.dialogs.remove.descriptionBoldWord")}</strong>
            {t("members.dialogs.remove.descriptionMiddle")}
            <strong>{member?.email}</strong>
            {t("members.dialogs.remove.descriptionSuffix")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("members.dialogs.remove.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t("members.dialogs.remove.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditEntitlementsDialog({
  member,
  onClose,
  onSave,
}: {
  member: Member | null;
  onClose: () => void;
  onSave: (member: Member, entitlements: string[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) {
      setSelected(new Set(member.entitlements));
    }
  }, [member]);

  function toggleEntitlement(ent: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ent)) {
        next.delete(ent);
      } else {
        next.add(ent);
      }
      return next;
    });
  }

  async function handleSave() {
    if (!member) return;
    setSaving(true);
    try {
      await onSave(member, Array.from(selected));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("members.dialogs.editEntitlements.title")}</DialogTitle>
          <DialogDescription>{t("members.dialogs.editEntitlements.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {KNOWN_ENTITLEMENTS.map((ent) => (
            <label key={ent} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.has(ent)}
                onChange={() => toggleEntitlement(ent)}
                className="h-4 w-4 rounded border-input"
                aria-label={ent}
              />
              <span className="text-sm">{ent}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("members.dialogs.editEntitlements.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="animate-spin" />
                {t("members.dialogs.editEntitlements.saving")}
              </>
            ) : (
              t("members.dialogs.editEntitlements.save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
